import React from 'react';
import { WebsiteDesignProps } from './types';
import { Ruler, Pencil, Compass, Construction } from 'lucide-react';
import { Button } from "@/components/ui/button";

const BlueprintArchitect: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Drafting Academy";

    return (
        <div className="bg-[#003B6F] text-white min-h-screen font-mono selection:bg-white selection:text-[#003B6F]">
            {/* Grid Pattern */}
            <div className="fixed inset-0 pointer-events-none opacity-20" style={{
                backgroundImage: 'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)',
                backgroundSize: '20px 20px'
            }}></div>
            <div className="fixed inset-0 pointer-events-none opacity-10" style={{
                backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
                backgroundSize: '100px 100px'
            }}></div>

            {/* Title Block */}
            <div className="fixed bottom-8 right-8 bg-[#003B6F] border-2 border-white p-4 w-64 z-50 shadow-lg">
                <div className="border border-white/50 p-2">
                    <div className="grid grid-cols-2 gap-2 text-[10px] uppercase">
                        <div className="border-b border-white/30 pb-1">Project</div>
                        <div className="border-b border-white/30 pb-1 font-bold">{schoolName}</div>
                        
                        <div className="border-b border-white/30 pb-1">Date</div>
                        <div className="border-b border-white/30 pb-1">24.10.2026</div>

                        <div className="border-b border-white/30 pb-1">Scale</div>
                        <div className="border-b border-white/30 pb-1">1:1</div>
                        
                        <div>Status</div>
                        <div className="text-green-300 font-bold">Approved</div>
                    </div>
                </div>
            </div>

            <nav className="relative z-20 p-8 border-b-2 border-white flex justify-between items-center text-white">
                <div className="flex items-center gap-3">
                    <div className="border-2 border-white rounded-full p-2"><Compass size={24} /></div>
                    <span className="text-2xl font-bold uppercase tracking-widest">{schoolName}</span>
                </div>
                <div className="flex gap-8 text-sm uppercase tracking-widest font-bold">
                    <a href="#" className="hover:underline decoration-2 underline-offset-4">Plan</a>
                    <a href="#" className="hover:underline decoration-2 underline-offset-4">Section</a>
                    <a href="#" className="hover:underline decoration-2 underline-offset-4">Detail</a>
                </div>
            </nav>

            <main className="container mx-auto px-12 py-20 relative z-10">
                <div className="border-2 border-dashed border-white/50 p-12">
                     {/* Dimension Lines */}
                     <div className="flex justify-between items-start mb-4 text-xs text-white/70">
                         <span>&lt;--- 100% WIDTH ---&gt;</span>
                     </div>

                     <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                         <div className="flex flex-col justify-center">
                             <h1 className="text-6xl font-bold mb-8 uppercase leading-tight relative">
                                 Design Your <br/> Future
                                 {/* Annotation */}
                                 <div className="absolute -top-8 -left-8 text-sm font-handwriting text-yellow-300 transform -rotate-12">
                                     Note: Font size critical for impact
                                 </div>
                             </h1>
                             <p className="text-lg text-white/80 font-light max-w-md mb-12">
                                 We provide the structural framework for success. Precise education engineered for the modern world.
                             </p>
                             <div className="relative inline-block w-fit">
                                 <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#003B6F] uppercase font-bold px-8 h-14 rounded-none text-lg">
                                     View Blueprints
                                 </Button>
                                 <div className="absolute -right-4 top-1/2 w-4 h-[2px] bg-white"></div>
                                 <div className="absolute left-1/2 -bottom-4 h-4 w-[2px] bg-white"></div>
                             </div>
                         </div>

                         <div className="relative">
                             <div className="border-2 border-white h-96 w-full relative overflow-hidden">
                                 <img src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=80&w=2071&auto=format&fit=crop" className="w-full h-full object-cover opacity-50 grayscale hover:opacity-80 transition-opacity" alt="Architect" />
                                 {/* Crosshairs */}
                                 <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                     <div className="w-full h-[1px] bg-white/50"></div>
                                     <div className="h-full w-[1px] bg-white/50 absolute"></div>
                                     <div className="w-20 h-20 border border-white/50 rounded-full bg-transparent absolute"></div>
                                 </div>
                             </div>
                             <div className="absolute -bottom-8 -right-8">
                                 <Construction size={48} className="text-white/20" />
                             </div>
                         </div>
                     </div>
                </div>

                <div className="mt-16 flex gap-12 text-white/60">
                     <div className="flex items-center gap-2">
                         <Ruler size={16} /> Precision Accuracy
                     </div>
                     <div className="flex items-center gap-2">
                         <Pencil size={16} /> Drafted by Experts
                     </div>
                </div>
            </main>
        </div>
    );
};

export default BlueprintArchitect;
